// /connect — a logged-in user allows ChatGPT or Claude to read their HandOff data (spec
// dashboard/buyer-insights-chatgpt-connector.md §4.1, §4.5). Full screen, outside the dashboard layout.
// Logged-out users get the login form on this same URL and come back here after login (App.js).

import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Link, Stack, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { ReactComponent as HandoffLogo } from '../../assets/handoff-logo.svg';
import { authService } from '../../services/auth-service';
import { dashboardAPI } from '../../services/dashboard-api';
import { assistantFor, connectCopy, errorMessage } from './copy';

export default function ConnectPage({ retailer }) {
  const { search } = useLocation();
  const query = search.replace(/^\?/, '');
  const who = assistantFor(search);
  const [state, setState] = useState(who ? 'ready' : 'error'); // ready | busy | done | error
  const [error, setError] = useState(who ? '' : errorMessage('bad_request'));

  useEffect(() => {
    document.title = 'Connect HandOff';
  }, []);

  const answer = async (allow) => {
    setState('busy');
    try {
      const { redirect } = await dashboardAPI.oauthConsent(query, allow);
      if (allow) setState('done');
      window.location.assign(redirect);
    } catch (e) {
      setError(errorMessage(e.code, who?.assistant));
      setState('error');
    }
  };

  const copy = who && connectCopy(who, { email: retailer?.email, shop: retailer?.name });

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="grey.100" px={2}>
      <Card sx={{ maxWidth: 520, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box mb={3} display="flex" justifyContent="center">
            <HandoffLogo style={{ height: 36, width: 'auto' }} />
          </Box>
          {copy && (
            <>
              <Typography variant="h5" component="h1" gutterBottom>
                {copy.heading}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                {copy.who} {copy.notYou}{' '}
                <Link component="button" variant="body2" onClick={() => authService.logout()}>
                  {copy.logOut}
                </Link>
              </Typography>
              {state !== 'error' && (
                <Stack spacing={1.5} mb={3}>
                  <Typography>{copy.what}</Typography>
                  <Typography>{copy.limits}</Typography>
                  <Typography>{copy.choice}</Typography>
                  <Typography color="text.secondary">{copy.stop}</Typography>
                </Stack>
              )}
            </>
          )}
          {state === 'error' && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {state === 'done' && <Alert severity="success">{copy.done}</Alert>}
          {(state === 'ready' || state === 'busy') && (
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" disabled={state === 'busy'} onClick={() => answer(false)}>
                {copy.cancel}
              </Button>
              <Button variant="contained" disabled={state === 'busy'} onClick={() => answer(true)}>
                {copy.allow}
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
